import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import {
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  PrinterIcon,
  CheckCircleIcon,
  LockClosedIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export default function AppointmentPrepPage() {
  const { user } = useAuth();
  const [brief, setBrief] = useState('');
  const [reportCount, setReportCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState('');

  async function generate() {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/premium/appointment-prep');
      setBrief(res.data.brief);
      setReportCount(res.data.reportCount);
      setGenerated(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate brief. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handlePrint() {
    window.print();
  }

  if (user?.subscriptionStatus === 'starter') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <ClipboardDocumentListIcon className="w-7 h-7 text-sky-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Appointment Prep requires Full Access</h2>
        <p className="text-slate-500 text-sm mb-6 max-w-sm mx-auto">
          Get an AI-generated summary of your lab history — ready to bring to your next doctor visit. Available on the Full Access plan.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-sky-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors"
        >
          <SparklesIcon className="w-4 h-4" />
          Upgrade to Full Access — $25/mo
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
            <ClipboardDocumentListIcon className="w-5 h-5 text-sky-600" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Appointment Prep Brief</h1>
        </div>
        <p className="text-slate-500 dark:text-slate-400 ml-13">
          AI-generated summary of your lab history — ready to bring to your next doctor visit.
        </p>
      </div>

      {!generated ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-8 text-center shadow-sm">
          <div className="w-16 h-16 bg-sky-50 dark:bg-sky-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardDocumentListIcon className="w-8 h-8 text-sky-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">Ready to prep for your appointment?</h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 max-w-md mx-auto">
            HealthClear AI will analyze your last 5 lab reports and create a personalized brief with
            key findings, questions to ask your doctor, and values to watch.
          </p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          )}
          <button
            onClick={generate}
            disabled={loading}
            className="bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2 mx-auto"
          >
            {loading ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Analyzing your lab history…
              </>
            ) : (
              <>
                <ClipboardDocumentListIcon className="w-4 h-4" />
                Generate My Appointment Brief
              </>
            )}
          </button>
        </div>
      ) : (
        <div>
          {/* Success banner */}
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">
              Generated from your {reportCount} most recent lab report{reportCount !== 1 ? 's' : ''}.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mb-6 print:hidden">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <PrinterIcon className="w-4 h-4" />
              Print / Save as PDF
            </button>
            <button
              onClick={() => { setGenerated(false); setBrief(''); }}
              className="flex items-center gap-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Regenerate
            </button>
          </div>

          {/* Brief content */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 sm:p-8 shadow-sm prose prose-slate max-w-none
            prose-h2:text-slate-800 dark:prose-h2:text-white prose-h2:text-lg prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-3
            prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed
            prose-li:text-slate-600 dark:prose-li:text-slate-300 prose-li:leading-relaxed
            prose-ul:my-3 prose-ol:my-3
            print:border-none print:shadow-none print:p-0">
            <div className="mb-4 print:block hidden">
              <p className="text-xs text-slate-400">Generated by HealthClear AI · {new Date().toLocaleDateString()}</p>
            </div>
            <ReactMarkdown>{brief}</ReactMarkdown>
          </div>

          <p className="mt-4 text-xs text-slate-400 text-center print:hidden">
            This brief is for informational purposes. Always discuss results with your licensed healthcare provider.
          </p>
        </div>
      )}
    </div>
  );
}
